import React, { useEffect, useState, useRef } from 'react';

const AI_AUTOMATION_URL = process.env.REACT_APP_AI_AUTOMATION_URL || 'http://localhost:8100';

/**
 * Google Calendar OAuth Callback Page
 * ------------------------------------
 * Google redirects here after the user grants calendar permission.
 * This page extracts the authorisation `code` from the URL and
 * forwards it to the backend endpoint that exchanges the code for
 * tokens. On success it notifies the opener window (the popup that
 * triggered the OAuth flow) and closes itself.
 *
 * NOTE: A useRef guard prevents React 18 StrictMode from sending
 * the authorization code twice (codes are single-use; a second
 * exchange attempt causes an `invalid_grant` error from Google).
 */
export default function CalendarCallbackPage() {
  const [status, setStatus] = useState('processing');
  const [error, setError] = useState(null);
  const [countdown, setCountdown] = useState(3);

  // Guard against React 18 StrictMode double-invocation of useEffect.
  // OAuth authorization codes are single-use — sending the code twice
  // causes Google to reject the second attempt with `invalid_grant`.
  const hasExchangedRef = useRef(false);

  useEffect(() => {
    // Prevent duplicate exchange (React StrictMode re-runs effects)
    if (hasExchangedRef.current) return;
    hasExchangedRef.current = true;

    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');
    const authError = params.get('error');

    if (authError) {
      const msg = authError === 'access_denied'
        ? 'You did not grant the required permissions. Please try again and make sure to check all permission boxes.'
        : `Google authorization failed: ${authError}`;
      setStatus('error');
      setError(msg);
      if (window.opener) {
        window.opener.postMessage({ type: 'GOOGLE_CALENDAR_CONNECTED', success: false, error: msg }, '*');
      }
      return;
    }

    if (!code) {
      setStatus('error');
      setError('No authorization code received from Google.');
      return;
    }

    // Build callback URL with both code and state (state carries user_id/cid)
    const callbackParams = new URLSearchParams({ code });
    if (state) {
      callbackParams.set('state', state);
    }

    // AI_AUTOMATION_URL may already include /api/v1 (from env) or not (fallback).
    // Normalise to avoid a double /api/v1/api/v1 prefix.
    const baseUrl = AI_AUTOMATION_URL.replace(/\/+$/, '');
    const callbackPath = baseUrl.endsWith('/api/v1')
      ? '/interviews/calendar/callback'
      : '/api/v1/interviews/calendar/callback';

    // Forward the code to the backend callback endpoint
    fetch(
      `${baseUrl}${callbackPath}?${callbackParams.toString()}`,
      { method: 'GET' }
    )
      .then(async (res) => {
        if (res.ok) {
          setStatus('success');
          // Notify the opener (popup parent) that calendar is connected
          if (window.opener) {
            window.opener.postMessage(
              { type: 'GOOGLE_CALENDAR_CONNECTED', success: true },
              '*'
            );
          }
          // Auto-close with countdown
          let count = 3;
          const timer = setInterval(() => {
            count -= 1;
            setCountdown(count);
            if (count <= 0) {
              clearInterval(timer);
              window.close();
            }
          }, 1000);
        } else {
          // Try to extract a meaningful error message from the response
          const text = await res.text();
          const match = text.match(/Error:\s*(.+?)<\/p>/);
          let errorMsg = match ? match[1] : 'Failed to connect Google Calendar. Please try again.';
          // Clean up prefixed error codes for display
          errorMsg = errorMsg.replace(/^MISSING_PERMISSIONS:\s*/i, '').replace(/^GOOGLE_PERMISSION_DENIED:\s*/i, '');
          setStatus('error');
          setError(errorMsg);
          // Notify parent window about the failure
          if (window.opener) {
            window.opener.postMessage(
              { type: 'GOOGLE_CALENDAR_CONNECTED', success: false, error: errorMsg },
              '*'
            );
          }
        }
      })
      .catch((err) => {
        setStatus('error');
        setError(`Network error: ${err.message}`);
      });
  }, []);

  return (
    <div style={styles.page}>
      {/* Animated background shapes */}
      <div style={styles.bgShape1} />
      <div style={styles.bgShape2} />

      <div style={styles.card}>
        {/* Google Calendar icon */}
        <div style={styles.iconWrap}>
          {status === 'processing' && (
            <div style={styles.spinnerOuter}>
              <div style={styles.spinnerInner} />
              <svg style={styles.calendarIcon} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="3" y="4" width="18" height="18" rx="3" stroke="#3B5BDB" strokeWidth="1.8" />
                <path d="M3 9h18" stroke="#3B5BDB" strokeWidth="1.8" />
                <path d="M8 2v4M16 2v4" stroke="#3B5BDB" strokeWidth="1.8" strokeLinecap="round" />
                <rect x="7" y="12" width="3" height="2.5" rx="0.5" fill="#3B5BDB" />
                <rect x="10.5" y="12" width="3" height="2.5" rx="0.5" fill="#3B5BDB" opacity="0.5" />
                <rect x="14" y="12" width="3" height="2.5" rx="0.5" fill="#3B5BDB" opacity="0.3" />
                <rect x="7" y="16" width="3" height="2.5" rx="0.5" fill="#3B5BDB" opacity="0.5" />
                <rect x="10.5" y="16" width="3" height="2.5" rx="0.5" fill="#3B5BDB" opacity="0.3" />
              </svg>
            </div>
          )}
          {status === 'success' && (
            <div style={styles.successCircle}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                <path d="M5 13l4 4L19 7" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
          )}
          {status === 'error' && (
            <div style={styles.errorCircle}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6l12 12" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
          )}
        </div>

        {/* Title */}
        <h1 style={styles.title}>
          {status === 'processing' && 'Connecting Google Calendar'}
          {status === 'success' && 'Calendar Connected!'}
          {status === 'error' && 'Connection Failed'}
        </h1>

        {/* Subtitle */}
        <p style={styles.subtitle}>
          {status === 'processing' && 'Setting up your calendar integration. This will only take a moment…'}
          {status === 'success' && 'Your Google Calendar is now linked to AgentFlow for scheduling interviews.'}
          {status === 'error' && (error || 'Something went wrong. Please try again.')}
        </p>

        {/* Footer / action area */}
        {status === 'processing' && (
          <div style={styles.progressBar}>
            <div style={styles.progressFill} />
          </div>
        )}

        {status === 'success' && (
          <p style={styles.closeNote}>
            This window will close in <strong>{countdown}s</strong>
          </p>
        )}

        {status === 'error' && (
          <button
            style={styles.retryBtn}
            onClick={() => window.close()}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#2D4BC1'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = '#3B5BDB'; }}
          >
            Close Window
          </button>
        )}
      </div>

      {/* Inline keyframe animations */}
      <style>{`
        @keyframes calendarSpin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes progressSlide {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(200%); }
        }
        @keyframes floatA {
          0%,100% { transform: translate(0,0) scale(1); }
          50% { transform: translate(20px,-30px) scale(1.05); }
        }
        @keyframes floatB {
          0%,100% { transform: translate(0,0) scale(1); }
          50% { transform: translate(-20px,20px) scale(1.08); }
        }
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(24px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

/* ── Inline styles ── */
const styles = {
  page: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #F0F4FF 0%, #E8F0FE 50%, #F5F3FF 100%)',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    overflow: 'hidden',
    padding: 24,
  },
  bgShape1: {
    position: 'absolute',
    top: '-15%',
    right: '-10%',
    width: 420,
    height: 420,
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(59,91,219,0.12) 0%, transparent 70%)',
    animation: 'floatA 6s ease-in-out infinite',
    pointerEvents: 'none',
  },
  bgShape2: {
    position: 'absolute',
    bottom: '-10%',
    left: '-8%',
    width: 340,
    height: 340,
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(32,201,151,0.10) 0%, transparent 70%)',
    animation: 'floatB 8s ease-in-out infinite',
    pointerEvents: 'none',
  },
  card: {
    position: 'relative',
    zIndex: 1,
    textAlign: 'center',
    padding: '48px 40px 40px',
    maxWidth: 420,
    width: '100%',
    background: '#fff',
    borderRadius: 20,
    boxShadow: '0 8px 40px rgba(59,91,219,0.08), 0 1px 3px rgba(0,0,0,0.04)',
    animation: 'fadeInUp 0.5s ease-out',
  },
  iconWrap: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: 24,
  },
  spinnerOuter: {
    position: 'relative',
    width: 64,
    height: 64,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  spinnerInner: {
    position: 'absolute',
    inset: 0,
    borderRadius: '50%',
    border: '3px solid #E5E7EB',
    borderTopColor: '#3B5BDB',
    animation: 'calendarSpin 1s linear infinite',
  },
  calendarIcon: {
    width: 28,
    height: 28,
  },
  successCircle: {
    width: 64,
    height: 64,
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #20C997, #12B886)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 4px 16px rgba(32,201,151,0.3)',
  },
  errorCircle: {
    width: 64,
    height: 64,
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #F56565, #E53E3E)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 4px 16px rgba(245,101,101,0.3)',
  },
  title: {
    fontSize: 22,
    fontWeight: 700,
    color: '#1A202C',
    margin: '0 0 8px',
    letterSpacing: '-0.01em',
  },
  subtitle: {
    fontSize: 14,
    lineHeight: 1.6,
    color: '#718096',
    margin: '0 0 28px',
    maxWidth: 320,
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  progressBar: {
    height: 4,
    borderRadius: 2,
    background: '#E5E7EB',
    overflow: 'hidden',
    maxWidth: 200,
    margin: '0 auto',
  },
  progressFill: {
    height: '100%',
    width: '50%',
    borderRadius: 2,
    background: 'linear-gradient(90deg, #3B5BDB, #20C997)',
    animation: 'progressSlide 1.5s ease-in-out infinite',
  },
  closeNote: {
    fontSize: 13,
    color: '#A0AEC0',
    margin: 0,
  },
  retryBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '10px 28px',
    fontSize: 14,
    fontWeight: 600,
    color: '#fff',
    background: '#3B5BDB',
    border: 'none',
    borderRadius: 10,
    cursor: 'pointer',
    transition: 'background 0.2s',
    boxShadow: '0 2px 8px rgba(59,91,219,0.25)',
  },
};
